// Fetch/fetchBuildingPayment.js

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7084';

// Helper function to get headers with auth token
const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// Helper function to handle API responses
const handleResponse = async (response) => {
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return await response.json();
    }
    
    return null; // For responses with no content (like DELETE)
};

// Get admin dashboard data
export const getAdminDashboard = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/admin/dashboard`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get all building payments
export const getBuildingPayments = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get building payment by ID
export const getBuildingPaymentById = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/${id}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get building payments by building ID
export const getBuildingPaymentsByBuildingId = async (buildingId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/building/${buildingId}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Create new building payment
export const createBuildingPayment = async (paymentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment`, {
            method: 'POST',
            headers: getAuthHeaders(),
            body: JSON.stringify(paymentData)
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Update building payment
export const updateBuildingPayment = async (id, paymentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/${id}`, {
            method: 'PUT',
            headers: getAuthHeaders(),
            body: JSON.stringify(paymentData)
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Delete building payment
export const deleteBuildingPayment = async (id) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/${id}`, {
            method: 'DELETE',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get payment summary for specific month/year
export const getPaymentSummary = async (year, month) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/summary/${year}/${month}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get unpaid buildings for specific month/year
export const getUnpaidBuildings = async (year, month) => {
    try {
        const response = await fetch(`${API_BASE_URL}/api/BuildingPayment/unpaid/${year}/${month}`, {
            method: 'GET',
            headers: getAuthHeaders()
        });
        
        return await handleResponse(response);
    } catch (error) {
        throw error;
    }
};

// Get current month payment summary
export const getCurrentMonthSummary = async () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    
    return await getPaymentSummary(year, month);
};

// Get current month unpaid buildings
export const getCurrentMonthUnpaid = async () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    
    return await getUnpaidBuildings(year, month);
};

// Bulk operations helper functions
export const createMultipleBuildingPayments = async (paymentsData) => {
    try {
        const promises = paymentsData.map(payment => createBuildingPayment(payment));
        const results = await Promise.allSettled(promises);
        
        const successful = results.filter(result => result.status === 'fulfilled');
        const failed = results.filter(result => result.status === 'rejected');
        
        return {
            successful: successful.length,
            failed: failed.length,
            total: paymentsData.length,
            errors: failed.map(result => result.reason.message)
        };
    } catch (error) {
        throw error;
    }
};

// Export all functions as a default object as well
export default {
    getAdminDashboard,
    getBuildingPayments,
    getBuildingPaymentById,
    getBuildingPaymentsByBuildingId,
    createBuildingPayment,
    updateBuildingPayment,
    deleteBuildingPayment,
    getPaymentSummary,
    getUnpaidBuildings,
    getCurrentMonthSummary,
    getCurrentMonthUnpaid,
    createMultipleBuildingPayments
};